import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2 } from "lucide-react";
import prismLogo from "@/assets/prism-logo.png";
import { ApiError } from "@/lib/api";
import { useAcceptInvite, useInviteInfo } from "@/lib/auth";
import { AuthChrome } from "@/components/auth/AuthChrome";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Spinner } from "@/components/ui/spinner";

interface Props {
  token: string;
  onSuccess: () => void;
}

export function ActivatePage({ token, onSuccess }: Props) {
  const { t } = useTranslation();
  const inviteQuery = useInviteInfo(token);
  const accept = useAcceptInvite();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError(t("activate.passwords_mismatch"));
      return;
    }

    try {
      await accept.mutateAsync({ token, new_password: password });
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail);
      } else {
        setError(t("login.error_unknown"));
      }
    }
  };

  /* Loading state — full canvas, no chrome controls needed yet */
  if (inviteQuery.isLoading) {
    return (
      <AuthChrome>
        <div className="flex flex-col items-center gap-3 text-fg-muted">
          <Spinner size="md" />
          <p className="text-sm">{t("activate.loading")}</p>
        </div>
      </AuthChrome>
    );
  }

  /* Error / expired invite state */
  if (inviteQuery.isError || !inviteQuery.data) {
    return (
      <AuthChrome>
        <Card className="w-full max-w-sm shadow-md">
          <CardContent className="pt-6">
            <Alert tone="danger" title={t("activate.invite_invalid_title")}>
              {t("activate.invite_invalid_body")}
            </Alert>
          </CardContent>
        </Card>
      </AuthChrome>
    );
  }

  const info = inviteQuery.data;

  return (
    <AuthChrome>
      <Card className="w-full max-w-sm shadow-md">
        <CardHeader className="pb-2">
          <img
            src={prismLogo}
            alt="Prism"
            className="mb-2 h-10 w-auto select-none"
            draggable={false}
          />
          <h1 className="text-2xl font-bold tracking-tight text-fg-primary">
            {info.purpose === "activation"
              ? t("activate.title_activate", { username: info.username })
              : t("activate.title_reset", { username: info.username })}
          </h1>

          {info.company_name && (
            <div className="mt-2 flex items-center gap-1.5 text-sm text-fg-muted">
              <Building2 size={14} strokeWidth={2} aria-hidden="true" className="shrink-0" />
              <span>
                {t("activate.company_label")}:{" "}
                <strong className="font-medium text-fg-secondary">{info.company_name}</strong>
              </span>
            </div>
          )}
        </CardHeader>

        <CardContent className="pt-2">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">{t("activate.new_password")}</Label>
              <PasswordInput
                id="new_password"
                autoComplete="new-password"
                minLength={12}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={accept.isPending}
                strengthMeter
              />
              <p className="text-xs text-fg-muted">
                {t("activate.password_requirements")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">{t("activate.confirm")}</Label>
              <PasswordInput
                id="confirm_password"
                autoComplete="new-password"
                minLength={12}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={accept.isPending}
                error={!!error && error === t("activate.passwords_mismatch")}
              />
            </div>

            {error && (
              <Alert tone="danger" icon>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full"
              loading={accept.isPending}
              size="lg"
            >
              {t("activate.submit")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthChrome>
  );
}
